// v4 AppNotice (VG1-gaps) — v3 src/components/ui/appNotice/AppNotice.tsx
// recomposed on tokens. v3 renders ONE notice banner whose message/severity
// cascades org-notice → masquerade-warning → native-app-version-status (last
// writer wins), plus an independent offline banner. Exam surfaces are NOT
// this component's job — v3 mounts AppNotice only in AppLayout; the take/
// manage tree carries its own alert cluster inside ExperienceLayout (v1).
import { DangerTriangleLinear, InfoCircleLinear } from '@assessprep/icons/solar';
import { ReactNode, useContext } from 'react';
import { useTranslation } from 'react-i18next';

import { useVisualViewportPin } from '@/lib/hooks/useVisualViewportPin';

import { UpdateAppButton } from '@/components/ap/lockdown/UpdateAppButton';
import { OnlineStatusContext } from '@/contexts/onlineStatus';
import { useCurrentUser, useCurrentUserOrg, useEnabledFeatures } from '@/hooks/dataHelpers';
import { roleMap } from '@/lib/appConstants';
import { cn } from '@/lib/cn';
import { checkMob } from '@/lib/index';
import {
  checkUserAppVersionStatus,
  getAppType,
  getAppVersion,
} from '@/lib/offline/OfflineAppHelper';
import { selectOriginalUserRole, useAuthStore } from '@/store';

type BannerTone = 'info' | 'error' | 'warning';

const TONE_CLASSES: Record<BannerTone, string> = {
  info: 'bg-muted text-foreground',
  error: 'bg-ap-destructive-a10 text-destructive',
  warning: 'bg-ap-warning-a10 text-ap-warning',
};

// R2-G45: on touch devices the stack is sticky (opaque canvas backdrop under
// the 10%-alpha washes) and rides useVisualViewportPin so the offline banner
// survives the iPad keyboard pan. Fine pointers keep today's in-flow render.
// Mounts only while a banner shows, so the pin's mount-time ref is always set.
const NoticeStack = ({ children }: { children: ReactNode }) => {
  const ref = useVisualViewportPin<HTMLDivElement>();
  return (
    <div
      ref={ref}
      data-testid="v4-app-notice"
      className="pointer-coarse:sticky pointer-coarse:top-0 pointer-coarse:z-30 pointer-coarse:bg-ap-canvas"
    >
      {children}
    </div>
  );
};

const Banner = ({
  tone,
  children,
  testId,
}: {
  tone: BannerTone;
  children: ReactNode;
  testId: string;
}) => (
  <div
    role="alert"
    data-testid={testId}
    className={cn(
      'flex min-h-9 flex-wrap items-center justify-center gap-x-2 gap-y-1 border-b border-border px-4 py-1.5 text-center text-sm',
      TONE_CLASSES[tone]
    )}
  >
    {tone === 'info' ? (
      <InfoCircleLinear aria-hidden="true" className="size-4 shrink-0" />
    ) : (
      <DangerTriangleLinear aria-hidden="true" className="size-4 shrink-0" />
    )}
    {children}
  </div>
);

export const AppNotice = () => {
  const { t } = useTranslation();
  const currentUser = useCurrentUser();
  const org = useCurrentUserOrg();
  const enabledFeatures = useEnabledFeatures();
  const originalUserRole = useAuthStore(selectOriginalUserRole);
  const { isOnline } = useContext(OnlineStatusContext);
  const appType = getAppType();
  const isMobile = checkMob();

  // ── the single cascading notice banner (v3 order, verbatim) ──
  const customFields = org?.custom_fields ?? {};
  let showNotice = !!customFields.show_notice && !isMobile && currentUser?.role === 'admin';
  let isError = false;
  let message: ReactNode = customFields.notice_message ?? '';
  if (customFields.notice_type === 'trialEnding') {
    message = t('AppNotice.your_trial_is_ending_soon_please_upgrade_to_continue_using');
  }
  if (customFields.notice_type === 'paymentDelay') {
    isError = true;
    message = t('AppNotice.your_invoice_is_due_please_process_the_invoice_immediately_to');
  }

  if (originalUserRole === 'superadmin' || originalUserRole === 'support') {
    isError = true;
    showNotice = true;
    message = t('AppNotice.logged_in_via_role_please_be_careful_while_making_any', {
      role: roleMap[originalUserRole ?? ''],
    });
  }

  if (appType !== 'web' && appType !== 'cros') {
    const appVersionStatus = checkUserAppVersionStatus(
      appType,
      { appVersion: getAppVersion(appType) },
      enabledFeatures
    );
    if (appVersionStatus?.status === 'unsupported' || appVersionStatus?.status === 'old') {
      isError = true;
      showNotice = true;
      message = (
        <>
          {t(
            appVersionStatus.status === 'unsupported'
              ? 'AppNotice.you_are_using_an_unsupported_version_of_the_application_please'
              : 'AppNotice.you_are_using_an_older_version_of_the_application_please'
          )}{' '}
          <UpdateAppButton size="sm" />
        </>
      );
    }
  }

  const showOffline = !isOnline;
  if (!showNotice && !showOffline) return null;

  return (
    <NoticeStack>
      {showNotice && (
        <Banner tone={isError ? 'error' : 'info'} testId="v4-app-notice-banner">
          {message}
        </Banner>
      )}
      {showOffline && (
        <Banner tone="warning" testId="v4-app-notice-offline">
          {t('CommonText.it_seems_you_are_not_connected_to_the_internet')}
        </Banner>
      )}
    </NoticeStack>
  );
};
